import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import SettingsSideBar from './components/settingsSideBar';
import './settings.scss';
import { formatTitle } from '../../../utils/utility';

/* ================== ORGANIZATION ================== */
import ProfileSettings from './organization/profile/profileSettings';
import Branding from './organization/branding/branding';
import Currencies from './organization/currencies/currencies';
import TaxSection from './organization/tax/taxSection';

import SubscriptionSandbox from './developer/subscriptionSandbox';

/* ================== PREFERENCES ================== */
import PreferencesGeneral from './preferences/general';

import Products from './inventory/products';
import Packaging from './inventory/packagings';
import MeasuringUnit from './inventory/measuringUnit';

/* ================== SALES ================== */
import SalesOrder from './sales/salesOrder';
import Packages from './sales/packages';
import Shipment from './sales/shipment';
import DeliveryChallans from './sales/deliveryChallans';
import SettingsInvoices from './sales/invoices';
import SettingsPaymentReceived from './sales/paymentReceived';
import SettingsSalesReturn from './sales/SalesReturn';
import SettingsCreditNote from './sales/creditNote';

/* ================== USERS & ROLES ================== */
import SettingsUsers from './users_and_roles/users';
import SettingsRoles from './users_and_roles/roles';

/* ================== PURCHASES ================== */
import SettingsPurchaseOrder from './purchases/purchase_order';
import SettingsPurchaseBills from './purchases/bills';
import SettingsPaymentsMade from './purchases/payments_made';
import SettingsPurchaseReceive from './purchases/purchase_receive';
import SettingsVendorCredit from './purchases/vendor_credit';
import Subscription from './organization/subscription';
import Accounts from './accounting/accounts';
import ParentAccounts from './accounting/parentAccounts';

export default function Settings() {
	const navigate = useNavigate();
	const suburl = localStorage.getItem('suburl');
	const param = useParams();
	const type = param?.type;
	const user = useSelector((state) => state?.userInfo?.userInfo?.data);
	const [activeTab, setActiveTab] = useState('');
	const [activeType, setActiveType] = useState('');
	const [searchTerm, setSearchTerm] = useState('');

	/* ================== SIDEBAR CONFIG ================== */
	const sidebarData = [
		{
			title: 'Organization',
			items: [
				{ label: 'Profile Settings', permission: 'settings.organisation.view' },
				{ label: 'Branding', permission: 'settings.organisation.view' },
				{ label: 'Currencies', permission: 'settings.organisation.view' },
				{ label: 'Tax', permission: 'settings.taxes.view' },
				{ label: 'My Subscription', permission: 'settings.my_subscription.view' },
			],
		},
		{
			title: 'Preferences',
			items: [{ label: 'General', permission: 'settings.general_preferences.view' }],
		},
		{
			title: 'Accounting',
			items: [
				{ label: 'Accounts', permission: 'settings.accounts.view' },
				{ label: 'Parent Accounts', permission: 'settings.parent_accounts.view' },
			],
		},
		{
			title: 'Inventory',
			items: [
				{ label: 'Products', permission: 'settings.inventory.view' },
				{ label: 'Packaging', permission: 'settings.inventory.view' },
				{ label: 'Measuring Unit', permission: 'settings.inventory.view' },
			],
		},
		{
			title: 'Sales',
			items: [
				{ label: 'Sales Order', permission: 'settings.sales.view' },
				{ label: 'Packages', permission: 'settings.sales.view' },
				{ label: 'Shipment', permission: 'settings.sales.view' },
				{ label: 'Delivery Challans', permission: 'settings.sales.view' },
				{ label: 'Invoices', permission: 'settings.sales.view' },
				{ label: 'Payments Received', permission: 'settings.sales.view' },
				{ label: 'Sales Return', permission: 'settings.sales.view' },
				{ label: 'Credit Note', permission: 'settings.sales.view' },
			],
		},
		{
			title: 'Purchases',
			items: [
				{ label: 'Bills', permission: 'settings.purchases.view' },
				{ label: 'Payments Made', permission: 'settings.purchases.view' },
				{ label: 'Purchase Orders', permission: 'settings.purchases.view' },
				{ label: 'Purchase Receive', permission: 'settings.purchases.view' },
				{ label: 'Vendor Credit', permission: 'settings.purchases.view' },
			],
		},
		{
			title: 'Users & Roles',
			items: [
				{ label: 'Users', permission: 'settings.users.view' },
				{ label: 'Roles', permission: 'settings.roles.view' },
			],
		},
	];

	/* ================== HELPERS ================== */
	const labelToType = (label) => label.replaceAll(' ', '-').toLowerCase();

	/* ================== FILTER SIDEBAR ================== */
	const filteredSidebarData = sidebarData
		.map((section) => {
			const permittedItems = section.items.filter(
				(item) =>
					user?.role?.role_permissions?.includes(item.permission) ||
					item.permission === 'settings.my_subscription.view',
			);

			const searchedItems = permittedItems.filter((item) =>
				item.label.toLowerCase().includes(searchTerm.toLowerCase()),
			);

			return { ...section, items: searchedItems };
		})
		.filter((section) => section.items.length > 0);

	/* ================== AUTO SELECT FIRST PERMITTED PAGE ================== */
	useEffect(() => {
		if (!filteredSidebarData.length) return;
		const isActiveAllowed = filteredSidebarData.some((section) =>
			section.items.some((item) => labelToType(item.label) === type),
		);

		if (!isActiveAllowed) {
			const firstItem = filteredSidebarData[0].items[0];
			const firstType = labelToType(firstItem.label);
			setActiveTab(firstItem.label);
			setActiveType(firstType);
			navigate(`/user/${suburl}/settings/${firstType}`);
		}
	}, []);

	/* ================== URL SYNC ================== */
	useEffect(() => {
		if (type) {
			setActiveType(type);
			setActiveTab(formatTitle(type));
		}
	}, [type]);

	/* ================== COMPONENT MAP ================== */
	const componentMap = {
		'profile-settings': ProfileSettings,
		branding: Branding,
		currencies: Currencies,
		tax: TaxSection,
		'my-subscription': Subscription,
		general: PreferencesGeneral,
		accounts: Accounts,
		'parent-accounts': ParentAccounts,
		'sales-order': SalesOrder,
		packages: Packages,
		shipment: Shipment,
		'delivery-challans': DeliveryChallans,
		invoices: SettingsInvoices,
		'payments-received': SettingsPaymentReceived,
		'sales-return': SettingsSalesReturn,
		'credit-note': SettingsCreditNote,
		users: SettingsUsers,
		roles: SettingsRoles,
		bills: SettingsPurchaseBills,
		'payments-made': SettingsPaymentsMade,
		'purchase-orders': SettingsPurchaseOrder,
		'purchase-receive': SettingsPurchaseReceive,
		'vendor-credit': SettingsVendorCredit,
		products: Products,
		'measuring-unit': MeasuringUnit,
		packaging: Packaging,
		// Read-only preview harness for plan changes. Registered like any other
		// settings page so it is reachable at settings/subscription-sandbox
		// without touching the sidebar permission config.
		'subscription-sandbox': SubscriptionSandbox,
	};

	const ActiveComponent = componentMap[activeType];

	/* ================== RENDER ================== */
	if (!filteredSidebarData.length && (searchTerm === '' || searchTerm === undefined)) {
		return (
			<div>
				<div style={{ backgroundColor: '#EDEDF7', position: 'sticky', top: 0 }}>
					<div className="fs-4 fw-semibold py-4 px-2">Settings</div>
				</div>
				<div
					className="d-flex justify-content-center align-items-center"
					style={{ marginTop: '250px' }}
				>
					<div className="fs-4 fw-semibold">
						Access denied. You don't have access to view Settings
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="settings-page">
			<div className="d-flex g-0 h-100">
				{/* Sidebar */}
				<div
					className="col-auto p-0 "
					style={{ width: '220px' }}
				>
					<SettingsSideBar
						activeTab={activeTab}
						setActiveTab={setActiveTab}
						setSearchTerm={setSearchTerm}
						searchTerm={searchTerm}
						filteredSidebarData={filteredSidebarData}
						sideBarType="settings"
					/>
				</div>

				{/* Content */}
				<div
					className="col settings-content"
					style={{
						minWidth: 0,
					}}
				>
					<div className="">
						{ActiveComponent ? <ActiveComponent activeTab={activeTab} /> : null}
					</div>
				</div>
			</div>
		</div>
	);
}
